import React, { useRef, useEffect, forwardRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { searchComponentFilteredDevicesV2, setLastScroll } from 'redux/actions';
import classNames from 'classnames';
import { Status } from 'common/utils/constants';
import { PerfectScrollbar } from '../ReactPerfectScrollbar';
import { ReactTable, ReactTableFixedColumns } from '../ReactTable';
import './ScrollReactTable.css';

const CustomTbodyComponent = (props) => {
  if (props.fetching) {
    return (
      <div id="GlobalTable_NoData" className={'GlobalTable_NoData'}>
        <svg className="spinner" viewBox="0 0 50 50">
          <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
        </svg>
      </div>
    );
  }

  return (
    <div id="rt-tbody" className={classNames('rt-tbody', props.className || [])}>
      <div id="ScrollReactTable_Container_Div" className={'ScrollReactTable_Container_Div'}>
        {props.overlay != null ? (
          <div id="ScrollReactTable_Overlay_Div" className={'ScrollReactTable_Overlay_Div'}>
            {props.overlay}
          </div>
        ) : null}
        {props.infinite && (
          <InfiniteScroll
            height={props.infiniteContainerHeight}
            dataLength={props.dataLength}
            next={props.fetchData}
            hasMore={props.hasMoreResults}
            loader={
              <div className="ScrollReactTable_InfiniteLoader" key={0}>
                <span>Loading ...</span>
              </div>
            }
            style={{ overflow: props.withFixedColumns ? 'unset' : 'auto' }} //add props.something here to set `unset` for device table only
            scrollableTarget={'ScrollReactTable_Container_InnerDiv'}
            useWindow={false}
          >
            <div
              id="ScrollReactTable_Container_InnerDiv"
              className="ScrollReactTable_Container_InnerDiv"
              style={{ display: props.isShowScrollReactTable ? 'block' : 'none' }}
            >
              {props.children}
            </div>
          </InfiniteScroll>
        )}
        {props.infinite == null && (
          // PerfectScrollbar is contradicting the wellscroll, which is not required
          // <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>
            <div>{props.children}</div>
          // </PerfectScrollbar>
        )}
      </div>
    </div>
  );
};

const CustomTableComponent = React.memo((props) => {
  const dispatch = useDispatch();
  const tableName = props.searchComponent;
  const tableStatus = useSelector(
    (store) => store.statuses[`searchComponentFilteredDevicesV2_${tableName}_${props.activeTab}`]
  );
  const lastScroll = useSelector((store) => store.data.lastScrollDictionary?.[props.activeTab]?.[tableName]);
  const tableId = tableName && props.activeTab != null ? `rt-table_${tableName}_${props.activeTab}` : 'rt-table';
  useEffect(() => {
    if (tableStatus === Status.SUCCESS) {
      let element = document.getElementById(tableId);
      element.scrollTop = lastScroll;
    }
  }, [tableId, tableStatus, lastScroll]);

  const scrollRef = useRef(lastScroll);
  const [localLastScroll, setLocalLastScroll] = useState(null);

  useEffect(() => {
    if (scrollRef?.current !== localLastScroll) {
      scrollRef.current = localLastScroll;
    }

    const timeOutId = setTimeout(() => {
      if (scrollRef?.current !== lastScroll && tableStatus !== Status.PENDING) {
        tableName &&
          scrollRef.current !== null &&
          dispatch(setLastScroll(scrollRef.current, tableName, props.activeTab));
      }
    }, 500);
    return () => clearTimeout(timeOutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLastScroll]);

  return (
    <div
      ref={props.lastScroll}
      id={tableId}
      className="rt-table"
      onScroll={(e) => {
        e.preventDefault();
        const scrollTop = e?.target?.scrollTop;
        const clientHeight = e?.target?.clientHeight;
        const scrollHeight = e?.target?.scrollHeight;

        setLocalLastScroll((prevVal) => {
          if (prevVal === null) {
            const element = document.getElementById(tableId);
            element.scrollTop = lastScroll;
            return lastScroll;
          } else {
            return scrollTop;
          }
        });

        if (!tableName || props.activeTab == null) {
          return;
        }

        if (scrollTop + clientHeight + 10 >= scrollHeight) {
          // reach bottom of the table and fetch additional 50 devices
          props.hasMoreResults &&
            dispatch(
              searchComponentFilteredDevicesV2(
                props.activeTab,
                tableName,
                props.searchFilteredDeviceTableBody,
                props.filterDictionary,
                props.devicesTableSort /*TODO swap in sorts*/,
                true
              )
            );
          dispatch(setLastScroll(scrollTop, tableName, props.activeTab));
        }
      }}
    >
      {props.children}
    </div>
  );
});

const NoDataComponent = forwardRef((props, ref) => {
  if (props.fetching) {
    return (
      <div ref={ref} id="GlobalTable_NoData" className={'GlobalTable_NoData'}>
        <svg id="spinner" className="spinner" viewBox="0 0 50 50">
          <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
        </svg>
      </div>
    );
  }

  return null;
});

export const ScrollReactTable = (props) => {
  const ref = React.useRef();
  const { withFixedColumns, forAlarmMap } = props;

  if (props.infinite && withFixedColumns && !forAlarmMap) {
    return (
      <ReactTableFixedColumns
        TableComponent={(inProps) => <CustomTableComponent {...props} {...inProps} />}
        TbodyComponent={(inProps) => <CustomTbodyComponent {...props} {...inProps} />}
        {...props}
      />
    );
  }

  if (withFixedColumns) {
    return <ReactTableFixedColumns NoDataComponent={() => <NoDataComponent {...props} ref={ref} />} {...props} />;
  }

  return (
    <ReactTable
      TableComponent={(inProps) => <CustomTableComponent {...props} {...inProps} />}
      TbodyComponent={(inProps) => <CustomTbodyComponent {...props} {...inProps} />}
      NoDataComponent={() => <NoDataComponent {...props} ref={ref} />}
      {...props}
    />
  );
};

export default connect((store) => {
  return {
    isShowScrollReactTable: store.style.isShowScrollReactTable
  };
})(ScrollReactTable);
